/**
 * Textos compartilhados por todo o app (não pertencem a um módulo só).
 * Por enquanto cobre apenas o seletor de idioma, que é interface nova. Os
 * botões, estados vazios e confirmações entram aqui nas próximas fatias.
 */
const common = {
  language: {
    label: 'Idioma',
    hint: 'Muda o idioma da interface. O conteúdo que você escreveu não é traduzido.',
  },
  selection: {
    /** Substantivo genérico, para quem não passa o próprio. */
    item: { one: 'item', other: 'itens' },
    selected: { one: '{{count}} item selecionado', other: '{{count}} itens selecionados' },
    delete: 'Excluir {{count}}',
    deleteTooltip: 'Excluir {{count}} {{item}}',
    clear: 'Limpar',
    clearTooltip: 'Limpar seleção',
    escHint: 'Esc para limpar seleção',
    confirmDelete: 'Tem certeza que deseja excluir {{count}} {{item}}?',
  },
  /**
   * Rótulos genéricos de ação, repetidos em modais e botões de todo o app.
   * Só entra aqui o que NÃO pertence a um módulo: `Cancelar` do FormModal é o
   * mesmo `Cancelar` do ConfirmModal, e duplicá-los por namespace só criaria
   * duas traduções para divergir.
   */
  actions: {
    confirm: 'Confirmar',
    cancel: 'Cancelar',
    save: 'Salvar',
    saving: 'Salvando...',
    wait: 'Aguarde...',
    select: 'Selecione...',
    retry: 'Tentar novamente',
  },

  /** Textos de componentes pequenos que atravessam o app. */
  help: {
    tooltip: 'Ajuda sobre este módulo',
  },
  /**
   * Banner de consentimento de cookies (LGPD). `message` termina sem ponto de
   * propósito: a frase continua no link e o ponto final fica depois dele.
   */
  cookies: {
    message: 'Utilizamos cookies para melhorar sua experiência e analisar o uso do site. Saiba mais na nossa',
    privacyLink: 'Política de Privacidade',
    reject: 'Rejeitar',
    accept: 'Aceitar',
  },
  errorBoundary: {
    title: 'Erro ao carregar módulo',
    description: 'Ocorreu um erro inesperado. Tente novamente ou mude para outro módulo.',
  },
  memberPicker: {
    primaryOwner: 'Responsável principal:',
    assign: 'Atribuir',
    empty: 'Nenhum membro encontrado',
  },
  comments: {
    title: 'Comentários',
    unknownUser: 'Usuário',
    delete: 'Excluir comentário',
    /** Rótulo do botão do composer. Enter envia, Shift+Enter quebra linha. */
    send: 'Enviar',
    placeholder: 'Escreva um comentário... Use @ para mencionar',
    now: 'agora',
    minutesAgo: 'há {{n}}min',
    hoursAgo: 'há {{n}}h',
    daysAgo: 'há {{n}}d',
  },
  /**
   * Textos do ProjectContext. Ficam aqui porque o contexto é regra, e o texto
   * dele é lido por `getCurrentCopy()`: as funções que os usam são helpers de
   * módulo ou closures de `useCallback`, onde hook não alcança.
   */
  undo: {
    dates: 'Alterar datas',
    rename: 'Renomear tarefa',
    progress: 'Alterar progresso',
    status: 'Alterar status',
    assigned: 'Alterar responsável',
    dependencies: 'Alterar dependências',
    color: 'Alterar cor',
    generic: 'Editar tarefa',
  },
  project: {
    loadTimeout: 'A conexão com o servidor demorou muito. Por favor, tente novamente.',
    loadError: 'Erro ao carregar projetos: {{message}}',
    unknownError: 'Erro desconhecido',
    notFound: 'Projeto não encontrado',
    invalidData: 'Dados do projeto inválidos',
    importColumnsFailed: 'As colunas do quadro não foram criadas. O projeto veio com o quadro padrão; ajuste em Kanban, Colunas.',
    copyOf: 'Cópia de {{name}}',
    today: 'Hoje',
    tomorrow: 'Amanhã',
  },
  /**
   * Erros de serviço que sobem para a tela. Ficam em `common` porque o mesmo
   * "Não autenticado" aparece em quatro serviços diferentes, e são lidos por
   * `getCurrentCopy()`: serviço não é componente.
   *
   * Validação de argumento de programador (priceId malformado, variável de
   * ambiente faltando) NÃO entra aqui: quem lê é desenvolvedor, não usuário.
   */
  /**
   * Mensagem de falha de exportação, por motivo. Antes havia UMA frase genérica
   * ("Erro ao exportar. Tente novamente.") para quatro causas distintas, e o detalhe
   * técnico ia só para o console, que o build de produção remove. O usuário não sabia o
   * que fazer e o suporte não sabia o que consertar.
   */
  exportErrors: {
    chunkLoad: 'Não foi possível carregar o exportador. Verifique a conexão e tente de novo.',
    elementMissing: 'Não encontramos o conteúdo para exportar. Recarregue a página e tente de novo.',
    unsupportedColor: 'Não foi possível desenhar o documento. Já avisamos o suporte.',
    canvasLimit: 'O período é grande demais para virar imagem. Reduza o intervalo de datas ou mude a escala para semanas ou meses.',
    canvasTaint: 'Uma imagem externa bloqueou a exportação. Remova anexos de outros sites e tente de novo.',
    pdfBuild: 'Não foi possível montar o PDF com essas opções de página.',
    workbookBuild: 'Não foi possível montar a planilha.',
    unknown: 'Erro ao exportar. Tente novamente.',
  },
  serviceErrors: {
    notAuthenticated: 'Não autenticado',
    userNotAuthenticated: 'Usuário não autenticado',
    deleteRequestFailed: 'Erro ao solicitar exclusão',
    deleteCancelFailed: 'Erro ao cancelar exclusão: {{message}}',
    aiUnavailable: 'Serviço de IA indisponível',
    aiDailyLimit: 'Limite diário atingido ({{limit}}/{{plan}}). Tente novamente amanhã.',
    aiFallback: 'Não foi possível gerar com IA. Usando template da biblioteca — tente novamente.',
    resourceMemberTaken: 'Este membro já está vinculado a outro recurso nesta organização.',
    resourceEmailTaken: 'Já existe um recurso com este email.',
    ganttElementMissing: 'Elemento do Gantt não encontrado',
    eapElementMissing: 'Elemento da EAP não encontrado',
    inviteNotFound: 'Convite não encontrado ou já utilizado',
    invitePending: 'Já existe um convite pendente para este email.',
    alreadyMember: 'Usuário já é membro deste projeto.',
    projectCreateNoId: 'Falha ao criar projeto: ID não retornado',
    projectNotPublished: 'Projeto não está publicado',
    projectLinkExpired: 'Projeto não encontrado ou link expirado',
    fileNotImage: 'Arquivo não é uma imagem',
  },
  /** Telas de borda do app: erro de conexão, app sem projeto, boot quebrado. */
  pwa: {
    updateTitle: 'Nova versão disponível',
    updateDescription: 'Recarregue quando terminar o que está fazendo.',
    updateAction: 'Recarregar',
    installTitle: 'Instalar o MeuGantt',
    installDescription: 'Abre direto da tela de início, sem barra do navegador.',
    installIOS: 'Toque em Compartilhar e escolha "Adicionar à Tela de Início".',
    installAction: 'Instalar',
    dismiss: 'Dispensar',
  },
  swipeRow: {
    aria: 'Deslize para a direita para concluir, para a esquerda para mover',
    concluir: 'Concluir',
    mover: 'Mover para',
  },
  hoje: {
    titulo: 'Tarefas',
    apoio: '{{n}} suas, em aberto',
    grupos: { atrasadas: 'Atrasadas', hoje: 'Vence hoje', depois: 'Depois' },
    concluida: 'Tarefa concluída',
    desfazer: 'Desfazer',
    movida: 'Tarefa movida',
    moverPara: 'Mover para',
    erroConcluir: 'Não foi possível concluir a tarefa',
    erroDesfazer: 'Não foi possível desfazer',
    erroMover: 'Não foi possível mover a tarefa',
    erroAbrir: 'Esta tarefa não existe mais',
    vazioTitulo: 'Nada vencendo por aqui',
    vazioDescricao: 'Nenhuma tarefa sua vence nos próximos sete dias. O que estiver mais adiante aparece na aba Projetos.',
  },
  /**
   * Equipe do projeto no celular: ver, convidar e remover.
   *
   * A grade densa de alocação pessoa por período continua só no computador. O
   * que vem para o telefone é a AÇÃO, não a tela.
   */
  equipeScreen: {
    titulo: "Equipe",
    apoio: "{{count}} pessoas neste projeto",
    apoioUm: "1 pessoa neste projeto",
    voltar: "Voltar para projetos",
    convidar: "Convidar pessoa",
    emailPlaceholder: "email@empresa.com",
    enviar: "Enviar",
    cancelar: "Cancelar",
    papel: "Papel",
    voce: "você",
    acoes: "Ações do membro",
    remover: "Remover do projeto",
    removerTitulo: "Remover do projeto",
    removerMensagem: "{{nome}} perde o acesso a este projeto. Você pode convidar de novo depois.",
    removido: "Membro removido",
    erroRemover: "Não foi possível remover",
    erroCarregar: "Não foi possível carregar a equipe",
    vazio: "Nenhum membro listado",
    semVaga: "Limite do plano atingido",
    contagem: "{{count}} de {{limit}} membros",
  },
  projetosScreen: {
    equipe: "Equipe",
    acoes: "Ações do projeto",
    favoritar: "Favoritar",
    desfavoritar: "Remover dos favoritos",
    renomear: "Renomear",
    salvar: "Salvar",
    duplicar: "Duplicar",
    duplicado: "Projeto duplicado",
    excluir: "Excluir",
    excluirTitulo: "Excluir projeto",
    excluirMensagem: 'O projeto "{{nome}}" e todas as tarefas dele serão apagados. Não há como desfazer.',
    excluido: "Projeto excluído",
    erroRenomear: "Não foi possível renomear",
    erroDuplicar: "Não foi possível duplicar",
    erroExcluir: "Não foi possível excluir",
    novoProjeto: 'Novo projeto',
    nomePlaceholder: 'Nome do projeto',
    criar: 'Criar',
    atualizar: 'Atualizar lista',
    erroCriar: 'Não foi possível criar o projeto',
    limiteAtingido: 'Você atingiu o limite de projetos do seu plano',
    titulo: 'Projetos',
    contagem: '{{n}} projetos',
    contagemUm: '1 projeto',
    pessoal: 'Pessoal',
    vazioTitulo: 'Nenhum projeto ainda',
    vazioDescricao: 'Crie o primeiro projeto no computador. Aqui no celular você acompanha e executa o que já existe.',
  },
  ajustesScreen: {
    titulo: 'Ajustes',
    configuracoes: 'Configurações',
    ajuda: 'Central de ajuda',
    temaEscuro: 'Tema escuro',
    sair: 'Sair da conta',
  },
  mobileTabs: {
    aria: 'Navegação principal',
    tarefas: 'Tarefas',
    projetos: 'Projetos',
    notificacoes: 'Notificações',
    ajustes: 'Ajustes',
  },
  desktopOnly: {
    title: '{{module}} funciona melhor no computador',
    description: 'Esta tela é de planejamento: precisa de largura para a grade, e no telefone ela viraria uma tabela ilegível. Abra no computador quando for planejar. Para tocar o dia a dia, o Kanban e Minhas Tarefas estão prontos aqui.',
    action: 'Ir para Hoje',
  },
  appShell: {
    connectionErrorTitle: 'Erro de Conexão',
    connectionErrorHint: 'Verifique se as políticas RLS do Supabase estão configuradas corretamente.',
    connectionErrorAction: 'Tentar Novamente',
    noProjectTitle: 'Nenhum projeto encontrado',
    noProjectDescription: 'Crie seu primeiro projeto para começar a gerenciar suas tarefas e cronogramas.',
    noProjectAction: 'Criar Primeiro Projeto',
    bootErrorTitle: 'Erro na Aplicação',
    bootErrorDescription: 'Algo deu errado. Verifique o console do navegador (F12) para mais detalhes.',
    bootErrorAction: 'Recarregar Página',
    /** Link de pular navegação e botão do menu mobile: só leitor de tela vê. */
    skipToContent: 'Ir para conteudo principal',
    openMenu: 'Abrir menu',
    shareTooltip: 'Compartilhar projeto com membros',
    settingsTooltip: 'Calendário do projeto: dias úteis, horas por dia e feriados',
    availableInPlan: 'Disponível no plano',
    close: 'Fechar',
    upgrade: 'Fazer Upgrade',
    searchMember: 'Buscar membro...',
    confirmDeleteProject: 'Tem certeza que deseja excluir este projeto? Esta ação não pode ser desfeita.',
    confirmDeleteLabel: 'Excluir',
    confirmRemoveFromOrg: 'Remover este projeto da organização? Ele voltará a ser um projeto pessoal.',
    confirmRemoveLabel: 'Remover',
  },
};

/** en e es derivam desta forma: chave faltando vira erro de compilação. */
export type CommonCopy = typeof common;

export default common;
